/**
 * Direct port of iOS CountingLadderCatalog + CountingGameValues.
 * Do not invent lessons, IDs, engines, or copy.
 */

export const COUNTING_COURSE_KEY = "counting";

export const COUNTING_ENGINES = [
  "numberPath",
  "tagCount",
  "matchRep",
  "sequenceRepair",
  "flashSet",
  "giveN",
  "countEvents",
  "compareSets",
  "frameFill",
  "hiddenSet",
  "traceNumeral",
  "rearrange",
];

function activity(
  number,
  title,
  engine,
  interaction,
  instruction,
  learningGoal,
  maximumNumber,
) {
  return {
    number,
    id: `counting-${String(number).padStart(2, "0")}`,
    title,
    engine,
    interaction,
    instruction,
    learningGoal,
    maximumNumber,
  };
}

export const COUNTING_LESSONS = [
  {
    number: 1,
    id: "counting-lesson-01",
    stage: "beforeCounting",
    title: "First Numbers",
    subtitle: "Touch, watch, and notice small amounts together",
    ageBand: "Ages 1–3 · grown-up and child together",
    activities: [
      activity(1, "Touch and It Appears", "tagCount", "tap", "Tap each duck once to count the friendly group.", "Cause and effect with number words", 3),
      activity(3, "The Counting Song", "countEvents", "tap", "Tap the drum and watch one star arrive each beat.", "One object for every beat", 3),
      activity(4, "Peekaboo Blueberries", "flashSet", "tap", "Remember the blueberries, then tap how many you saw.", "Quickly see quantities to three", 3),
      activity(5, "Same-Same", "matchRep", "tap", "Tap the number that matches the blueberries.", "Match a quantity across different objects", 3),
      activity(6, "Blueberries or Flowers", "compareSets", "tap", "Tap the group with more blueberries or flowers.", "See more and fewer", 4),
      activity(7, "Blueberry Basket", "giveN", "tapAndTap", "Put the right number of blueberries in the basket.", "Make a small matching set", 3),
    ],
  },
  {
    number: 2,
    id: "counting-lesson-02",
    stage: "beforeCounting",
    title: "The Number Song",
    subtitle: "Put number words in a steady order",
    ageBand: "Ages 2½–3½",
    activities: [
      activity(9, "Call and Answer", "countEvents", "tap", "Tap the drum to add the next beat.", "Continue a number rhythm", 5),
      activity(11, "Where’d It Go", "sequenceRepair", "tap", "Tap the number that fills the empty place.", "Find the missing number", 10),
      activity(12, "Flower Number Match", "matchRep", "tap", "Count the flowers, then tap the matching number.", "Recognize the next number", 10),
      activity(13, "Missing Number", "sequenceRepair", "tapAndTap", "Tap the number that fills the empty place.", "Put sizes in order", 5),
    ],
  },
  {
    number: 3,
    id: "counting-lesson-03",
    stage: "beforeCounting",
    title: "One Touch, One Thing",
    subtitle: "Give every object exactly one count",
    ageBand: "Ages 3–4",
    activities: [
      activity(14, "Count the Duck Line", "tagCount", "tap", "Tap each duck once to count the whole line.", "One touch for each object", 5),
      activity(15, "Count the Sheep", "tagCount", "tapAndTap", "Tap each sheep once to count the whole group.", "Keep counted and uncounted objects apart", 5),
      activity(17, "Slow Down", "tagCount", "tap", "Tap each star once, one at a time.", "Match one deliberate touch to one count", 5),
      activity(18, "Off the Line", "tagCount", "tap", "Find and tap each scattered leaf once.", "Keep track in a scattered group", 6),
    ],
  },
  {
    number: 4,
    id: "counting-lesson-04",
    stage: "countingMeaning",
    title: "So How Many?",
    subtitle: "The last count tells how many altogether",
    ageBand: "Ages 3½–4½",
    activities: [
      activity(19, "Count All the Stars", "tagCount", "tapAndTap", "Tap each star once to count how many altogether.", "Use the last count as the total", 6),
      activity(20, "Grow the Pile", "tagCount", "tap", "Tap each bead once to grow the count.", "See that one more makes the next number", 6),
      activity(22, "Cover and Ask", "hiddenSet", "tap", "Remember how many are hiding under the cloth.", "Keep the total without recounting", 6),
      activity(23, "Count the Ducklings", "tagCount", "tapAndTap", "Tap each duck once to count all three ducks.", "Tell position from quantity", 5),
      activity(25, "The Shop", "giveN", "tapAndTap", "Put the requested blueberries in the basket.", "Make and judge an exact set", 6),
      activity(27, "Fill the Blueberry Basket", "giveN", "tapAndTap", "Add the requested blueberries to the basket.", "Use counting to verify", 6),
    ],
  },
  {
    number: 6,
    id: "counting-lesson-06",
    stage: "countingMeaning",
    title: "The Shapes of Numbers",
    subtitle: "Connect numerals, words, and quantities",
    ageBand: "Ages 3½–5½ · independent track",
    activities: [
      activity(29, "Numeral Jigsaw", "traceNumeral", "drag", "Drag the glowing bead along the large numeral.", "Recognize numeral shapes", 9),
      activity(30, "Follow the Path", "traceNumeral", "drag", "Start at the green dot and trace the large numeral.", "Form numerals in stroke order", 9),
      activity(31, "Blueberry Number Match", "matchRep", "tapAndTap", "Count the blueberries, then tap the matching number.", "Connect ways to show one value", 9),
      activity(32, "Flower Number Match", "matchRep", "tap", "Count the flowers, then tap the matching number.", "Find quantity across representations", 9),
      activity(33, "Blueberry Numeral Hunt", "matchRep", "tap", "Count the blueberries, then find their number.", "Tell numeral shapes apart", 9),
      activity(34, "Flower Number Match", "matchRep", "tap", "Count the flowers, then tap their number.", "Connect number words to symbols", 9),
    ],
  },
  {
    number: 7,
    id: "counting-lesson-07",
    stage: "countingMeaning",
    title: "Things You Can’t Hold",
    subtitle: "Count beats, jumps, arrivals, and mixed things",
    ageBand: "Ages 4–5½",
    activities: [
      activity(35, "Count the Drums", "countEvents", "tap", "Tap the drum once for every beat.", "Count sounds over time", 6),
      activity(36, "How Many Jumps", "countEvents", "tap", "Tap the rabbit once for every jump.", "Count actions over time", 6),
      activity(37, "Mixed Bag", "tagCount", "tap", "Tap each different thing once.", "Count a mixed collection", 6),
      activity(38, "Rabbits Under Cover", "hiddenSet", "tap", "Remember how many rabbits are hiding under the cloth.", "Track a changing hidden set", 6),
      activity(39, "Sleepy Drum Count", "countEvents", "tap", "Tap the drum once for every sleep you count.", "Count events across a short story", 5),
    ],
  },
  {
    number: 8,
    id: "counting-lesson-08",
    stage: "countingMeaning",
    title: "Start Anywhere",
    subtitle: "A set stays the same in any order",
    ageBand: "Ages 4½–5½",
    activities: [
      activity(40, "Count the Stars", "tagCount", "tapAndTap", "Tap each star once to count the whole group.", "See that order does not change the total", 10),
      activity(43, "Did It Change?", "rearrange", "tap", "Watch the stones spread out, then choose the total.", "See that spacing does not change number", 8),
      activity(44, "Pebble Count", "tagCount", "tap", "Tap each pebble once to count the whole group.", "Count past a misleading arrangement", 8),
    ],
  },
  {
    number: 9,
    id: "counting-lesson-09",
    stage: "structure",
    title: "Five and Ten",
    subtitle: "See numbers as friendly parts",
    ageBand: "Ages 4½–6",
    activities: [
      activity(45, "Blueberry Flash", "flashSet", "tap", "Remember the blueberries, then tap how many you saw.", "See structured quantities to ten", 10),
      activity(46, "Blueberry Total", "flashSet", "tap", "Remember the blueberries, then tap their total.", "Notice different valid number parts", 10),
      activity(47, "How Many More", "frameFill", "tap", "Tap the empty spaces needed to fill ten.", "Find how many more make ten", 10),
      activity(48, "Bead Slide", "frameFill", "drag", "Drag the beads across until the frame shows ten.", "Compose quantities in groups of five", 10),
      activity(49, "Quick Blueberries", "flashSet", "tap", "Remember the blueberries, then tap how many you saw.", "Connect finger patterns to quantity", 10),
      activity(50, "Flower Total", "matchRep", "tapAndTap", "Count the flowers, then tap their total.", "See equal totals split different ways", 10),
    ],
  },
  {
    number: 11,
    id: "counting-lesson-11",
    stage: "structure",
    title: "Eleven Is a Liar",
    subtitle: "Build teen numbers as ten and some more",
    ageBand: "Ages 5–6½",
    activities: [
      activity(57, "Ten and Some More", "frameFill", "tap", "Fill ten, then add the extra ones.", "See teen numbers as ten plus some", 20),
      activity(58, "Teen Trouble", "sequenceRepair", "tap", "Repair the missing teen number.", "Practice the irregular teen sequence", 20),
      activity(59, "Thirteen or Thirty", "matchRep", "tap", "Match the number-word card to the right numeral.", "Tell teen and decade names apart", 50),
      activity(61, "Teen Blueberry Count", "matchRep", "tap", "Count the blueberries, then tap their number.", "Translate a teen word into digits", 20),
      activity(63, "Blueberry Count", "matchRep", "tap", "Count the blueberries, then tap their number.", "Know number neighbors", 20),
      activity(67, "Fill the Gaps", "sequenceRepair", "tap", "Fill gaps in a strip that starts above one.", "Use the number sequence flexibly", 20),
      activity(74, "Missing Decade", "sequenceRepair", "tap", "Tap the missing number between forty and sixty.", "Learn the decade-name sequence", 100),
    ],
  },
  {
    number: 13,
    id: "counting-lesson-13",
    stage: "countingTool",
    title: "Parts Make Wholes",
    subtitle: "Find the smaller numbers inside a number",
    ageBand: "Ages 5½–7½",
    activities: [
      activity(68, "How Many Are Hiding", "hiddenSet", "tap", "Some pebbles are showing. Tap how many stay hidden.", "Find a missing part", 10),
      activity(71, "Make Ten", "frameFill", "drag", "Drag in exactly enough beads to make ten.", "Build bonds to ten", 10),
      activity(72, "Make the Groups Match", "compareSets", "drag", "Drag the block to make both groups match.", "See equality as balance", 10),
      activity(79, "Build the Number Word", "sequenceRepair", "tapAndTap", "Build the shown number-word card with hundreds, tens, and ones.", "Translate place-value words into digits", 999),
    ],
  },
];

export const COUNTING_ACTIVITIES = COUNTING_LESSONS.flatMap((lesson) =>
  lesson.activities.map((act) => ({ ...act, lesson: lesson.number })),
);

export const COUNTING_ACTIVITY_IDS = new Set(COUNTING_ACTIVITIES.map((a) => a.id));
export const COUNTING_LESSON_IDS = new Set(COUNTING_LESSONS.map((l) => l.id));

/** Exact iOS CountingLadderProgressStore prerequisite graph. */
export const COUNTING_PREREQUISITES = {
  1: [],
  2: [],
  3: [2],
  4: [3],
  6: [],
  7: [4],
  8: [4, 7],
  9: [1, 4],
  11: [2, 9],
  13: [9, 11],
};

const TARGET_OVERRIDES = {
  1: 3,
  2: 2,
  23: 3,
  26: 4,
  42: 10,
  43: 6,
  45: 7,
  47: 10,
  48: 10,
  71: 10,
  52: 7,
  56: 47,
  57: 13,
  58: 16,
  60: 16,
  61: 16,
  59: 13,
  62: 16,
  67: 16,
  64: 10,
  65: 6,
  68: 7,
  74: 50,
  75: 30,
  78: 100,
  79: 645,
};

export function countingActivityById(id) {
  return COUNTING_ACTIVITIES.find((a) => a.id === id) || null;
}

export function countingLessonByNumber(number) {
  return COUNTING_LESSONS.find((l) => l.number === Number(number)) || null;
}

export function countingLessonContaining(activityId) {
  return (
    COUNTING_LESSONS.find((lesson) =>
      lesson.activities.some((a) => a.id === activityId),
    ) || null
  );
}

/** CountingGameValues.target(for:) */
export function countingTarget(activity) {
  const number = typeof activity === "number" ? activity : activity.number;
  const maximumNumber =
    typeof activity === "number"
      ? countingActivityById(`counting-${String(number).padStart(2, "0")}`)
          ?.maximumNumber ?? number
      : activity.maximumNumber;
  if (TARGET_OVERRIDES[number] != null) return TARGET_OVERRIDES[number];
  return Math.min(Math.max(2, 2 + (number % 7)), Math.max(2, maximumNumber));
}

/** CountingGameValues.choices(around:maximum:) */
export function countingChoices(target, maximum) {
  const ceiling = Math.max(2, maximum);
  const low = Math.max(0, target - 1);
  const high = Math.min(ceiling, target + 1);
  return Array.from(new Set([low, target, high])).sort((a, b) => a - b);
}

/** CountingGameValues.hiddenComposition(for:) */
export function countingHiddenComposition(activity) {
  const total = countingTarget(activity);
  if (activity.number === 68) {
    const showing = Math.min(3, total - 1);
    return { covered: total - showing, showing, total };
  }
  return { covered: total, showing: 0, total };
}

export function countingSequenceStep(activity) {
  return activity.number === 74 ? 10 : 1;
}

export function countingSequenceValues(activity) {
  const target = countingTarget(activity);
  const step = countingSequenceStep(activity);
  return {
    target,
    step,
    beforeValue: Math.max(0, target - step),
    afterValue: target + step,
  };
}

/** Deterministic distractors from CountingSequenceGame.sequenceChoices */
export function countingSequenceChoices(activity) {
  const { target, step, beforeValue, afterValue } =
    countingSequenceValues(activity);
  const maximum = Math.max(activity.maximumNumber, target);
  const shownValues = new Set([beforeValue, target, afterValue]);
  const offsets = [-3, 3, -2, 2, -4, 4, -5, 5];
  const distractors = [];

  for (const offset of offsets) {
    const candidate = target + offset * step;
    if (
      candidate >= 0 &&
      candidate <= maximum &&
      !shownValues.has(candidate) &&
      !distractors.includes(candidate)
    ) {
      distractors.push(candidate);
      if (distractors.length === 2) break;
    }
  }

  if (distractors.length < 2) {
    for (let candidate = 0; candidate <= maximum; candidate += 1) {
      if (!shownValues.has(candidate) && !distractors.includes(candidate)) {
        distractors.push(candidate);
        if (distractors.length === 2) break;
      }
    }
  }

  const choices = [target, ...distractors.slice(0, 2)];
  const rotation = activity.number % choices.length;
  return choices.slice(rotation).concat(choices.slice(0, rotation));
}

export function countingMatchChoices(activity) {
  const target = countingTarget(activity);
  if (activity.number === 59) return [13, 30];
  if (activity.number === 79) return [645, 600, 405];
  return countingChoices(target, activity.maximumNumber);
}

export function countingFrameConfig(activity) {
  const target = countingTarget(activity);
  let startingCount = Math.min(6, target - 1);
  if (activity.number === 57) startingCount = 10;
  if (activity.number === 48) startingCount = 5;
  return {
    target,
    startingCount,
    totalSlots: activity.number === 57 ? 20 : 10,
  };
}

export function countingCompareSizes(activity) {
  const larger = countingTarget(activity);
  const smaller =
    activity.number === 6
      ? 1
      : Math.max(1, larger - ([54, 72].includes(activity.number) ? 1 : 2));
  return { larger, smaller };
}

export function countingFlashDelay(activity) {
  return activity.number === 45 ? 0.8 : 1.2;
}

export function countingTagPiece(activity, index) {
  switch (activity.number) {
    case 15:
      return "sheep";
    case 17:
    case 19:
    case 40:
      return "star";
    case 18:
      return "leaf";
    case 20:
      return "bead";
    case 37: {
      const mixed = ["duck", "flower", "blueberry", "leaf", "pebble", "rabbit"];
      return mixed[index % mixed.length];
    }
    case 42:
    case 44:
      return "pebble";
    default:
      return "duck";
  }
}

export function countingMatchPiece(activity) {
  return activity.number % 2 === 0 ? "flower" : "blueberry";
}

export function countingNumberPathValues(activity) {
  if (activity.maximumNumber > 20) {
    const values = [];
    for (let n = 0; n <= 100; n += 10) values.push(n);
    return values;
  }
  if (activity.number === 60 || activity.number === 65) {
    return [10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
  }
  return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
}

export function countingTraceNumeral(activity) {
  return countingTarget(activity) % 10;
}

export const COUNTING_TRACE_START = {
  0: { x: 0.5, y: 0.28 },
  1: { x: 0.5, y: 0.29 },
  2: { x: 0.28, y: 0.29 },
  3: { x: 0.25, y: 0.28 },
  4: { x: 0.41, y: 0.29 },
  5: { x: 0.72, y: 0.29 },
  6: { x: 0.66, y: 0.29 },
  7: { x: 0.25, y: 0.3 },
  8: { x: 0.5, y: 0.28 },
  9: { x: 0.36, y: 0.29 },
};

export const COUNTING_SPOKEN_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
];

export function countingSpokenWord(value) {
  return COUNTING_SPOKEN_WORDS[value] ?? String(value);
}

export const COUNTING_STAGE_ACCENT = {
  togetherTime: "#FFC221",
  beforeCounting: "#75D1FA",
  countingMeaning: "#3DC791",
  structure: "#946EF2",
  countingTool: "#FF636E",
};

export const COUNTING_ENGINE_THEME = {
  numberPath: {
    name: "Rainbow Steps",
    accent: "#946EF2",
    secondary: "#75D1FA",
    hero: "step",
    panoramaOffset: -0.16,
  },
  tagCount: {
    name: "Duckling Meadow",
    accent: "#FFC221",
    secondary: "#3DC791",
    hero: "duck",
    panoramaOffset: 0.14,
  },
  matchRep: {
    name: "Berry Match Garden",
    accent: "#75D1FA",
    secondary: "#946EF2",
    hero: "blueberry",
    panoramaOffset: -0.24,
  },
  sequenceRepair: {
    name: "Block Builder Grove",
    accent: "#FF636E",
    secondary: "#FFC221",
    hero: "cube",
    panoramaOffset: 0.22,
  },
  flashSet: {
    name: "Peekaboo Patch",
    accent: "#3DC791",
    secondary: "#75D1FA",
    hero: "leaf",
    panoramaOffset: -0.08,
  },
  giveN: {
    name: "Picnic Basket Park",
    accent: "#FF636E",
    secondary: "#FFC221",
    hero: "basket",
    panoramaOffset: 0.27,
  },
  countEvents: {
    name: "Starry Music Hill",
    accent: "#946EF2",
    secondary: "#FFC221",
    hero: "drum",
    panoramaOffset: -0.3,
  },
  compareSets: {
    name: "More-or-Less Market",
    accent: "#3DC791",
    secondary: "#FF636E",
    hero: "basket",
    panoramaOffset: 0.08,
  },
  frameFill: {
    name: "Bead Rainbow Workshop",
    accent: "#75D1FA",
    secondary: "#FFC221",
    hero: "bead",
    panoramaOffset: -0.2,
  },
  hiddenSet: {
    name: "Mystery Pebble Hollow",
    accent: "#946EF2",
    secondary: "#3DC791",
    hero: "cloth",
    panoramaOffset: -0.12,
  },
  traceNumeral: {
    name: "Crayon Sky Studio",
    accent: "#75D1FA",
    secondary: "#946EF2",
    hero: "crayon",
    panoramaOffset: -0.27,
  },
  rearrange: {
    name: "Tumbling Stone Garden",
    accent: "#3DC791",
    secondary: "#75D1FA",
    hero: "pebble",
    panoramaOffset: 0.12,
  },
};

export function countingBackdropAsset(lessonNumber) {
  if (lessonNumber >= 1 && lessonNumber <= 3) {
    return "worlds/counting_meadow_world.png";
  }
  if (lessonNumber === 4 || lessonNumber === 6) {
    return "worlds/counting_number_garden.png";
  }
  if (lessonNumber === 7 || lessonNumber === 8) {
    return "shared/counting_rhythm_sky.png";
  }
  if (lessonNumber === 9 || lessonNumber === 11) {
    return "worlds/counting_ten_town.png";
  }
  return "shared/counting_builder_bay.png";
}
